import type { UserPermission } from '../../../../utils/genericTypes'

const EDIT_SUFFIX = '_Edit'

export interface AuthorizedFunctionRow {
  name: string
  viewName: string
  editName: string
  hasView: boolean
  hasEdit: boolean
  offersView: boolean
  offersEdit: boolean
  viewLocked: boolean
  editLocked: boolean
  depth: number
}

const viewKeyOf = (permissionKey: string): string =>
  permissionKey.endsWith(EDIT_SUFFIX) ? permissionKey.slice(0, -EDIT_SUFFIX.length) : permissionKey

/**
 * The rows for what a membership actually holds, from the permission keys
 * `User/UserFunctionsByOrganization` returns (`CETA Participation_CETA Teacher_Edit`).
 *
 * `UserOrganization.userFunctions` is **not** a substitute: `GetUserDetails` collapses each grant to
 * its distinct leaf name with the Edit suffix stripped, so View and Edit are indistinguishable there
 * and every row would read Edit=Off.
 */
export const heldFunctionRows = (permissionKeys: string[]): AuthorizedFunctionRow[] => {
  const rows: AuthorizedFunctionRow[] = []

  ;[...permissionKeys].sort().forEach((permissionKey) => {
    const viewName = viewKeyOf(permissionKey)
    const isEdit = permissionKey.endsWith(EDIT_SUFFIX)
    const segments = viewName.split('_')

    const existing = rows.find((row) => row.viewName === viewName)
    if (existing != null) {
      if (isEdit) existing.hasEdit = true
      else existing.hasView = true
      return
    }

    rows.push({
      name: segments[segments.length - 1],
      viewName,
      editName: `${viewName}${EDIT_SUFFIX}`,
      hasView: !isEdit,
      hasEdit: isEdit,
      offersView: true,
      offersEdit: true,
      viewLocked: false,
      editLocked: false,
      depth: segments.length - 1
    })
  })

  return rows
}

/**
 * The rows an administrator may grant: every function the organization's role offers, whether or not
 * the user holds it. `UserPermissionsByRole` marks implicit permissions, which come with the role and
 * cannot be toggled off.
 */
export const grantableFunctionRows = (
  permissions: UserPermission[],
  selectedPermissionKeys: string[]
): AuthorizedFunctionRow[] => {
  const rows: AuthorizedFunctionRow[] = []

  const collect = (options: UserPermission[], depth: number): void => {
    options.forEach((option) => {
      const viewName = option.readPermissionName ?? ''
      const editName = option.writePermissionName ?? ''

      rows.push({
        name: option.nodeName,
        viewName,
        editName,
        hasView: selectedPermissionKeys.includes(viewName),
        hasEdit: selectedPermissionKeys.includes(editName),
        offersView: viewName !== '',
        offersEdit: editName !== '',
        viewLocked: option.readImplicit,
        editLocked: option.writeImplicit,
        depth
      })

      collect(option.subPermissions, depth + 1)
    })
  }

  collect(permissions, 0)

  return rows
}

const permissionKeysFor = (
  permissions: UserPermission[],
  predicate: (option: UserPermission) => { view: boolean, edit: boolean }
): string[] => {
  const keys: string[] = []

  const collect = (options: UserPermission[]): void => {
    options.forEach((option) => {
      const wanted = predicate(option)
      if (wanted.view && option.readPermissionName != null) keys.push(option.readPermissionName)
      if (wanted.edit && option.writePermissionName != null) keys.push(option.writePermissionName)
      collect(option.subPermissions)
    })
  }

  collect(permissions)

  return keys
}

const implicitPermissionKeys = (permissions: UserPermission[]): string[] =>
  permissionKeysFor(permissions, (option) => ({ view: option.readImplicit, edit: option.writeImplicit }))

/**
 * A role selects its implicit functions and nothing else. Explicit grants are deliberately not carried
 * across a role change: on a new card the outgoing selection is only the default role's implicit set, so
 * carrying it forward silently granted whatever the incoming role merely offers — an ISD card defaults to
 * ISD User, whose implicit CETA Teacher is explicit for ISD Admin, and the switch handed it over unasked.
 */
export const selectionForRole = (permissions: UserPermission[]): string[] =>
  implicitPermissionKeys(permissions)

/** Pairing across the permission tree: granting a child grants its ancestors, revoking one revokes its descendants. */
export const togglePermissionTreeFunction = (selected: string[], permissionKey: string, checked: boolean): string[] => {
  const segments = permissionKey.split('_')
  const isEdit = segments[segments.length - 1] === 'Edit'
  if (isEdit) segments.pop()

  if (!checked) {
    const withoutBranch = selected.filter((key) => !key.startsWith(permissionKey))
    if (!isEdit) return withoutBranch

    const viewKey = segments.join('_')
    return withoutBranch.filter((key) => !key.startsWith(viewKey) || !key.endsWith(EDIT_SUFFIX))
  }

  const granted = [...selected]
  while (segments.length > 0) {
    const viewKey = segments.join('_')
    if (!granted.includes(viewKey)) granted.push(viewKey)

    const editKey = `${viewKey}${EDIT_SUFFIX}`
    if (isEdit && !granted.includes(editKey)) granted.push(editKey)

    segments.pop()
  }

  return granted
}
